<template lang="pug">
div
  yz-divider.mt-2
  .d-flex.align-center.mt-4
    div
      .text-lg.font-medium.neutral700--text Self-pay payment method
      .text-sm.neutral700--text Add, edit, remove or change status of your self-pay payments
    v-spacer
    yz-btn(label="All branches" outlined size="lg")

  v-row.mt-6.pb-2.pb-md-0(no-gutters)
    v-col(cols="12" md="7")
      yz-tabs(v-model="tabs" type="line")
        yz-tab(:label="$t('all')")
        yz-tab(label="Active")
        yz-tab(label="Archived")
    v-col(cols="12" md="5" align="end")
      .d-flex.align-center
        v-spacer
        yz-search-btn(v-model="searchText" size="lg" @search="queryChanged")
        yz-btn.ml-4(label="Add new" size="lg" @click="addNewPayMethod")

  yz-divider

  .data-table.mt-2
    yz-data-table(:headers="headers" :items="paymentMethods" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" no-outer-border radius="0" disable-sort body-height="52px" header-height="36px" header-background-color="white" @click:row="editItem")
      //- price tier
      template(v-slot:item.price_tier="{ item }")
        yz-truncated-list(v-if="item.price_tiers && item.price_tiers.length" :items="item.price_tiers" text-item="name" :gap="4")
      //- branches
      template(v-slot:item.branches="{ item }")
        yz-badge(border) All branches
      //- status
      template(v-slot:item.status="{ item }")
        div(@click.stop)
          yz-switch(v-model="item.enabled" size="md" @change="updateStatus(item, item.enabled)")
      //- actions
      template(v-slot:item.actions="{ item, index }")
        div.d-flex(@click.stop)
          v-spacer
          v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg")
            template(#activator="{ on }")
              yz-btn(icon-only icon="mdi-dots-horizontal" icon-size="24" size="sm" icon-color="neutral-500" v-on="on")
            yz-list.py-0
              yz-list-item(@click="editItem(item)")
                div Edit
              yz-divider
              yz-list-item(@click="deleteItem(item)")
                div.danger500--text Delete

  div.d-flex.align-center(v-if="totalCount > pageSize")
    v-spacer
    yz-pagination(v-model="currentPage" version="yc" :page-size.sync="pageSize" :count="totalCount" @input="pageChanged")

  .dialogs
    add-self-pay-dialog(v-if="openAddSelfPayDialog" :open.sync="openAddSelfPayDialog" :parentPayment="selectedPayMethod" @closed="closed" @reset="reset")
    delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="closed" @delete="confirmDelete")
      .text-lg.font-medium Are you sure you want to delete this self-pay payment?
      .text-md.mt-4 This action will be done immediately and can't be undone. Click "Confirm" to proceed

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddSelfPayDialog from '@/components/clinic/settings/AddSelfPayDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
export default {
  components: { AddSelfPayDialog, DeleteConfirmationDialog },
  data: () => ({
    isFetching: false,
    isLoading: false,
    openAddSelfPayDialog: false,
    openDeleteDialog: false,
    selectedPayMethod: null,
    tabs: 0,
    pageSize: 10,
    searchText: '',
    totalCount: 0,
    currentPage: 1,
    paymentMethods: [],
    headers: [
      { text: 'NAME', value: 'name', enable: true },
      { text: 'PRICE TIER', value: 'price_tier', enable: true },
      { text: 'BRANCHES', value: 'branches', enable: true },
      { text: 'STATUS', value: 'status', enable: true, width: 50 },
      { text: 'ACTIONS', value: 'actions', enable: true, class: 'text-end', width: '100', sortable: false },
    ]
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
  },
  watch: {
    tabs () {
      this.searchText = null
      this.currentPage = 1
      this.fetchSelfPayMethods()
    },
    pageSize () {
      this.currentPage = 1
      this.fetchSelfPayMethods()
    }
  },
  mounted () {
    this.fetchSelfPayMethods()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchSelfPayMethods () {
      this.isFetching = true
      let params = {
        page_size: this.pageSize,
        page: this.currentPage,
        company: this.company.id,
        channel: 'self-pay'
      }
      if (this.tabs === 1) { params.enabled = true }
      if (this.tabs === 2) { params.enabled = false }
      if (this.searchText) { params.search = this.searchText }
      this.$axios.get(`/payment_methods/`, { params })
        .then(resp => {
          this.paymentMethods = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    updateStatus (item, status) {
      this.isFetching = true
      this.$axios.patch(`/payment_methods/${item.id}`, { enabled: status })
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isFetching = false
        })
    },
    confirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/payment_methods/${this.selectedPayMethod.id}`)
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    queryChanged () {
      this.currentPage = 1
      this.fetchSelfPayMethods()
    },
    pageChanged () {
      this.fetchSelfPayMethods()
    },
    addNewPayMethod () {
      this.openAddSelfPayDialog = true
    },
    editItem (item) {
      this.selectedPayMethod = item
      this.openAddSelfPayDialog = true
    },
    deleteItem (item) {
      this.selectedPayMethod = item
      this.openDeleteDialog = true
    },
    reset () {
      this.currentPage = 1
      this.fetchSelfPayMethods()
      this.selectedPayMethod = null
      this.openDeleteDialog = false
      this.openAddSelfPayDialog = false
    },
    closed () {
      this.selectedPayMethod = null
      this.openDeleteDialog = false
      this.openAddSelfPayDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 0) {
        let start = this.paymentMethods.length - 2
        if (position > start) {
          return true
        }
      }
      return false
    },
  }
}
</script>

<style lang="scss" scoped>
.custom-width {
  width: 50px !important;
}
.data-table ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover > td {
  cursor: pointer;
  background: var(--yz-neutral-200) !important;
}
</style>
